import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

export default function HistoryImportPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [file, setFile] = useState(null);
  const [gpsFrom, setGpsFrom] = useState("");
  const [gpsTo, setGpsTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {}); }, []);

  const runPreview = async () => {
    if (!companyId) { toast.error("Choisissez une société"); return; }
    if (!file) { toast.error("Choisissez un fichier CSV/Excel"); return; }
    setLoading(true); setPreview(null); setResult(null); setMapping({});
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("company_id", companyId);
      if (gpsFrom) fd.append("gps_from", gpsFrom);
      if (gpsTo) fd.append("gps_to", gpsTo);
      const r = await api.post("/admin/history-import/preview", fd);
      setPreview(r.data);
      toast.success("Prévisualisation prête — aucune donnée écrite");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la prévisualisation"); }
    finally { setLoading(false); }
  };

  const toggleMap = (ref, field, value) => {
    setMapping((m) => ({ ...m, [ref]: { ...(m[ref] || {}), [field]: value } }));
  };

  const runConfirm = async () => {
    if (!preview) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/history-import/confirm", {
        preview_id: preview.preview_id, confirm: true, mapping,
      });
      setResult(r.data);
      setPreview(null);
      toast.success(`Import terminé : ${r.data.totals.imported} position(s) importée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'import"); }
    finally { setLoading(false); }
  };

  const unmatched = (preview?.units || []).filter((u) => u.device_ref && !u.matched);

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="history-import-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Upload className="w-6 h-6" /> Import historique (3Dtracking)</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Migration des historiques depuis app.boomerang.nc. Correspondance par identifiant boîtier exact
            (IMEI ou Cellocator). <b>Prévisualisation obligatoire</b> puis <b>confirmation séparée</b>. Aucune alerte
            ni email rétroactif ; une position live plus récente n'est jamais écrasée.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">Société cible
              <select data-testid="hist-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                      className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                <option value="">— Choisir —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-sm">Fichier (.csv / .xlsx)
              <input type="file" accept=".csv,.xlsx,.xlsm" data-testid="hist-file"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="mt-1 w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-2" />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">Borne GPS début <span className="text-muted-foreground">(optionnel)</span>
              <input type="text" data-testid="hist-gps-from" value={gpsFrom} onChange={(e) => setGpsFrom(e.target.value)}
                     placeholder="31 Oct 2026 18:40:00 UTC+11 ou 2026-10-31T07:40:00Z"
                     className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </label>
            <label className="text-sm">Borne GPS fin <span className="text-muted-foreground">(optionnel)</span>
              <input type="text" data-testid="hist-gps-to" value={gpsTo} onChange={(e) => setGpsTo(e.target.value)}
                     placeholder="03 Nov 2026 18:40:00 UTC+11 ou 2026-11-03T07:40:00Z"
                     className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">Les rapports source filtrent sur « Date du serveur » ; renseignez ici les bornes <b>Date GPS</b> explicites. Les lignes hors bornes sont signalées (non importées), jamais écartées en silence.</p>
          <div className="flex items-center gap-2">
            <button data-testid="hist-preview-btn" onClick={runPreview} disabled={loading}
                    className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Prévisualiser (dry-run)
            </button>
            <button data-testid="hist-confirm-btn" onClick={runConfirm} disabled={loading || !preview}
                    className="px-4 py-2 rounded-md border border-border text-sm font-medium disabled:opacity-40 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Confirmer l'import
            </button>
          </div>
        </div>

        {preview && (
          <div className="rounded-lg border border-border bg-card p-4 space-y-4" data-testid="hist-preview">
            <div className="flex flex-wrap gap-3 text-sm">
              {Object.entries({ "Importables": preview.totals.importable, "Doublons": preview.totals.duplicates,
                "Rejetées": preview.totals.rejected, "Techniques (sans GNSS valide)": preview.totals.technical,
                "Hors fenêtre GPS": preview.totals.out_of_window, "Unités non reconnues": preview.totals.unmatched_units }).map(([k, v]) => (
                <div key={k} className="px-3 py-2 rounded-md bg-black/5"><b>{v}</b> <span className="text-muted-foreground">{k}</span></div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hist-units-table">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1 pr-3">Identifiant boîtier</th><th className="py-1 pr-3">Unité</th>
                  <th className="py-1 pr-3">Importables</th><th className="py-1 pr-3">Doublons</th>
                  <th className="py-1 pr-3">Techniques</th><th className="py-1 pr-3">Rejetées</th>
                </tr></thead>
                <tbody>
                  {preview.units.map((u) => (
                    <tr key={u.device_ref || "vide"} className="border-b border-border/40">
                      <td className="py-1 pr-3 font-mono text-xs">{u.device_ref || "(vide)"}</td>
                      <td className="py-1 pr-3">{u.matched
                        ? <span className="text-green-600">{u.matched.name}</span>
                        : <span className="text-amber-600">non reconnue</span>}</td>
                      <td className="py-1 pr-3">{u.importable}</td><td className="py-1 pr-3">{u.duplicates}</td>
                      <td className="py-1 pr-3">{u.technical}</td><td className="py-1 pr-3">{u.rejected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {unmatched.length > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="hist-mapping">
                <div className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />
                  Table de correspondance (création d'unités manquantes — cochez pour créer) :</div>
                {unmatched.map((u) => (
                  <div key={u.device_ref} className="flex flex-wrap items-center gap-2 text-sm">
                    <label className="flex items-center gap-1">
                      <input type="checkbox" data-testid={`hist-map-create-${u.device_ref}`}
                             checked={!!mapping[u.device_ref]?.create}
                             onChange={(e) => toggleMap(u.device_ref, "create", e.target.checked)} />
                      <span className="font-mono text-xs">{u.device_ref}</span>
                    </label>
                    <input placeholder="Nom de l'unité" data-testid={`hist-map-name-${u.device_ref}`}
                           onChange={(e) => toggleMap(u.device_ref, "name", e.target.value)}
                           className="rounded border border-border bg-background px-2 py-1 text-xs" />
                    <input placeholder="Plaque" data-testid={`hist-map-plate-${u.device_ref}`}
                           onChange={(e) => toggleMap(u.device_ref, "plate", e.target.value)}
                           className="rounded border border-border bg-background px-2 py-1 text-xs w-28" />
                  </div>
                ))}
              </div>
            )}

            {preview.rejected_samples?.length > 0 && (
              <div className="text-xs text-muted-foreground" data-testid="hist-rejected">
                <b>Exemples de rejets :</b> {preview.rejected_samples.slice(0, 8).map((r, i) => (
                  <span key={i} className="mr-2">[{r.reason}]</span>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Info className="w-3 h-3" />
              Prévisualisation uniquement — aucune donnée écrite tant que vous ne confirmez pas.</p>
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-green-600/40 bg-green-600/5 p-4 space-y-2" data-testid="hist-result">
            <div className="text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" />
              Import terminé : <b>{result.totals.imported}</b> importée(s), <b>{result.totals.duplicates}</b> doublon(s),
              <b>{result.totals.created_units}</b> unité(s) créée(s), <b>{result.totals.last_position_updated}</b> position(s) live mise(s) à jour.</div>
            <p className="text-[11px] text-muted-foreground">{result.note}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
